import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Icon } from '@iconify/react'
import { useAuth } from '@/hooks/useAuth'
import { fetchCampaignById, type Campaign, type CampaignUpsertInput } from '@/lib/campaigns'
import { fetchCombos } from '@/lib/combos'
import { env } from '@/lib/env'
import type { Combo } from '@/types/combo'
import { WidgetModalLivePreview } from './WidgetModalLivePreview'
import {
  clampWidgetRadiusPx,
  escapeHtmlAttribute,
  normalizeHex6,
  previewHexForLiveDisplay,
  tryParseCompleteHex,
  widgetApiBaseUrl,
} from './widgetPreviewTokens'
import {
  moneyValuesClose,
  parseApiMoney,
  resolveWidgetMaxExtra,
  resolveWidgetMinExtra,
  resolveWidgetUnitPrice,
} from './widgetModalPreviewLogic'
import styles from './CampaignWidgetManager.module.css'

export type LiveCampaignFieldsForWidgetPreview = {
  price: string
  min_quantity_per_purchase: string
  max_quantity_per_purchase: string
}

export type WidgetEmbedFormSlice = Pick<
  CampaignUpsertInput,
  | 'widget_embed_button_text'
  | 'widget_embed_button_color'
  | 'widget_embed_button_text_color'
  | 'widget_embed_button_radius_px'
>

interface CampaignWidgetManagerProps {
  campaignId: number
  widgetEmbed: WidgetEmbedFormSlice
  landingButtonColor: string
  onWidgetEmbedChange: (patch: Partial<WidgetEmbedFormSlice>) => void
  liveCampaignFields?: LiveCampaignFieldsForWidgetPreview
}

const FALLBACK_BUTTON = '#5e17eb'
const FALLBACK_TEXT = '#ffffff'
const FALLBACK_RADIUS = 8

export function CampaignWidgetManager({
  campaignId,
  widgetEmbed,
  landingButtonColor,
  onWidgetEmbedChange,
  liveCampaignFields,
}: CampaignWidgetManagerProps) {
  const { token } = useAuth()
  const landingBtn = normalizeHex6(landingButtonColor, FALLBACK_BUTTON)

  const [copied, setCopied] = useState(false)

  const [campaignSnapshot, setCampaignSnapshot] = useState<Campaign | null>(null)
  const [combosSnapshot, setCombosSnapshot] = useState<Combo[]>([])
  const [previewLoadError, setPreviewLoadError] = useState<string | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(true)

  const buttonText = widgetEmbed.widget_embed_button_text ?? ''
  const buttonColorDraft = widgetEmbed.widget_embed_button_color ?? ''
  const buttonTextColorDraft = widgetEmbed.widget_embed_button_text_color ?? ''
  const buttonRadius = widgetEmbed.widget_embed_button_radius_px ?? ''

  const fetchPreviewSnapshot = useCallback(async () => {
    setIsPreviewLoading(true)
    setPreviewLoadError(null)
    try {
      const [campaign, combos] = await Promise.all([
        fetchCampaignById(campaignId, token ?? undefined),
        fetchCombos(campaignId, true),
      ])
      setCampaignSnapshot(campaign)
      setCombosSnapshot(combos)
      setIsPreviewLoading(false)
    } catch {
      setCampaignSnapshot(null)
      setCombosSnapshot([])
      setPreviewLoadError('Não foi possível carregar campanha ou combos para a prévia.')
      setIsPreviewLoading(false)
    }
  }, [campaignId, token])

  useEffect(() => {
    void fetchPreviewSnapshot()
  }, [fetchPreviewSnapshot])

  const apiUrl = widgetApiBaseUrl()
  const scriptUrl = `${apiUrl}/js/widget.js?v=${encodeURIComponent(env.widgetAssetVersion)}`

  const radiusPx = useMemo(() => clampWidgetRadiusPx(buttonRadius, FALLBACK_RADIUS), [buttonRadius])

  const colorPickerButton = useMemo(
    () => tryParseCompleteHex(buttonColorDraft) ?? landingBtn,
    [buttonColorDraft, landingBtn],
  )
  const colorPickerText = useMemo(
    () => tryParseCompleteHex(buttonTextColorDraft) ?? FALLBACK_TEXT,
    [buttonTextColorDraft],
  )

  const effectiveButtonColor = useMemo(() => {
    const committed = tryParseCompleteHex(buttonColorDraft) ?? landingBtn
    return previewHexForLiveDisplay(buttonColorDraft, committed, FALLBACK_BUTTON)
  }, [buttonColorDraft, landingBtn])

  const effectiveTextColor = useMemo(() => {
    const committed = tryParseCompleteHex(buttonTextColorDraft) ?? FALLBACK_TEXT
    return previewHexForLiveDisplay(buttonTextColorDraft, committed, FALLBACK_TEXT)
  }, [buttonTextColorDraft])

  const unitPrice = useMemo(
    () =>
      resolveWidgetUnitPrice(liveCampaignFields?.price, campaignSnapshot?.price),
    [liveCampaignFields?.price, campaignSnapshot?.price],
  )

  const minExtra = useMemo(
    () =>
      resolveWidgetMinExtra(
        liveCampaignFields?.min_quantity_per_purchase,
        campaignSnapshot?.min_quantity_per_purchase,
      ),
    [liveCampaignFields?.min_quantity_per_purchase, campaignSnapshot?.min_quantity_per_purchase],
  )

  const maxExtra = useMemo(
    () =>
      resolveWidgetMaxExtra(
        liveCampaignFields?.max_quantity_per_purchase,
        campaignSnapshot?.max_quantity_per_purchase,
      ),
    [liveCampaignFields?.max_quantity_per_purchase, campaignSnapshot?.max_quantity_per_purchase],
  )

  const preferServerComboPrices = useMemo(() => {
    if (!campaignSnapshot) return false
    const savedUnit = parseApiMoney(campaignSnapshot.price)
    return moneyValuesClose(unitPrice, savedUnit)
  }, [campaignSnapshot, unitPrice])

  const snippet = useMemo(() => {
    const label = buttonText.trim() || 'Comprar Números'
    const safeText = escapeHtmlAttribute(label)
    const safeApi = escapeHtmlAttribute(apiUrl)
    const safeScript = escapeHtmlAttribute(scriptUrl)
    return `<script 
  src="${safeScript}" 
  data-campaign-id="${campaignId}" 
  data-api-url="${safeApi}"
  data-button-text="${safeText}" 
  data-button-color="${effectiveButtonColor}" 
  data-button-text-color="${effectiveTextColor}" 
  data-button-radius="${radiusPx}px">
</script>`
  }, [
    scriptUrl,
    campaignId,
    apiUrl,
    buttonText,
    effectiveButtonColor,
    effectiveTextColor,
    radiusPx,
  ])

  const copyToClipboard = () => {
    void navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    })
  }

  const handleButtonColorTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    onWidgetEmbedChange({ widget_embed_button_color: e.target.value })
  }

  const handleTextColorTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    onWidgetEmbedChange({ widget_embed_button_text_color: e.target.value })
  }

  const commitButtonColor = () => {
    const next = normalizeHex6(buttonColorDraft, landingBtn)
    onWidgetEmbedChange({ widget_embed_button_color: next })
  }

  const commitTextColor = () => {
    const next = normalizeHex6(buttonTextColorDraft, FALLBACK_TEXT)
    onWidgetEmbedChange({ widget_embed_button_text_color: next })
  }

  const showPreviewRefresh = previewLoadError !== null && !isPreviewLoading

  return (
    <div className={styles.wrapper}>
      <div className={styles.introCard} role="note">
        <Icon icon="mdi:information-outline" className={styles.introIcon} aria-hidden />
        <div className={styles.introBody}>
          <p className={styles.introLead}>
            Incorpore o widget no HTML do seu site ou landing page para vender números da sorte sem redirecionar o
            visitante para outro domínio.
          </p>
          <p className={styles.introSecondary}>
            O script carrega o botão e o fluxo de compra em um modal. O endereço inclui{' '}
            <code className={styles.inlineCode}>?v=…</code> para reduzir cache antigo após atualizações no servidor.
            Ao publicar nova versão do widget, copie de novo o código e substitua no seu site.
          </p>
        </div>
      </div>

      <div className={styles.mainGrid}>
        <section className={styles.card} aria-labelledby="widget-config-heading">
          <div className={styles.cardHeader}>
            <Icon icon="mdi:tune-variant" className={styles.cardHeaderIcon} aria-hidden />
            <div>
              <h3 id="widget-config-heading" className={styles.cardTitle}>
                Aparência do botão
              </h3>
              <p className={styles.cardSubtitle}>
                Esses valores alimentam os atributos data-* do script e o modal. Salve a campanha no topo para manter
                estas opções na conta.
              </p>
            </div>
          </div>

          <div className={styles.cardBody}>
            <div className={styles.configFieldsRow}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="widget-btn-text">
                  Texto do botão
                </label>
                <input
                  id="widget-btn-text"
                  type="text"
                  className={styles.input}
                  value={buttonText}
                  onChange={(e) => onWidgetEmbedChange({ widget_embed_button_text: e.target.value })}
                  placeholder="Ex.: Comprar números"
                  autoComplete="off"
                />
              </div>

              <div className={styles.field}>
                <span className={styles.label} id="widget-btn-color-label">
                  Cor do botão
                </span>
                <div className={styles.colorRow}>
                  <input
                    type="color"
                    className={styles.colorSwatch}
                    value={colorPickerButton}
                    onChange={(e) => {
                      const next = normalizeHex6(e.target.value, landingBtn)
                      onWidgetEmbedChange({ widget_embed_button_color: next })
                    }}
                    aria-labelledby="widget-btn-color-label"
                  />
                  <input
                    type="text"
                    className={styles.input}
                    value={buttonColorDraft}
                    onChange={handleButtonColorTextChange}
                    onBlur={commitButtonColor}
                    aria-labelledby="widget-btn-color-label"
                    spellCheck={false}
                  />
                </div>
              </div>

              <div className={styles.field}>
                <span className={styles.label} id="widget-text-color-label">
                  Cor do texto
                </span>
                <div className={styles.colorRow}>
                  <input
                    type="color"
                    className={styles.colorSwatch}
                    value={colorPickerText}
                    onChange={(e) => {
                      const next = normalizeHex6(e.target.value, FALLBACK_TEXT)
                      onWidgetEmbedChange({ widget_embed_button_text_color: next })
                    }}
                    aria-labelledby="widget-text-color-label"
                  />
                  <input
                    type="text"
                    className={styles.input}
                    value={buttonTextColorDraft}
                    onChange={handleTextColorTextChange}
                    onBlur={commitTextColor}
                    aria-labelledby="widget-text-color-label"
                    spellCheck={false}
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="widget-radius">
                  Arredondamento (px)
                </label>
                <input
                  id="widget-radius"
                  type="number"
                  className={styles.input}
                  value={buttonRadius}
                  onChange={(e) => onWidgetEmbedChange({ widget_embed_button_radius_px: e.target.value })}
                  min={0}
                  max={50}
                />
              </div>
            </div>
          </div>
        </section>

        <div className={styles.previewPairRow}>
          <section className={styles.card} aria-labelledby="widget-btn-preview-heading">
            <div className={styles.cardHeader}>
              <Icon icon="mdi:gesture-tap-button" className={styles.cardHeaderIcon} aria-hidden />
              <div>
                <h3 id="widget-btn-preview-heading" className={styles.cardTitle}>
                  Prévia do botão
                </h3>
                <p className={styles.cardSubtitle}>Como o visitante verá o CTA antes de abrir o modal.</p>
              </div>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.buttonPreviewWell}>
                <div className={styles.buttonPreviewCanvas}>
                  <button
                    type="button"
                    className={styles.previewButton}
                    style={{
                      backgroundColor: effectiveButtonColor,
                      color: effectiveTextColor,
                      borderRadius: `${radiusPx}px`,
                    }}
                  >
                    {buttonText || 'Comprar números'}
                  </button>
                </div>
                <p className={styles.previewHint}>
                  No site real, um clique abre o modal de compra com combos, quantidade e pagamento PIX.
                </p>
              </div>
            </div>
          </section>

          <section className={styles.card} aria-labelledby="widget-modal-preview-heading">
            <div className={styles.cardHeader}>
              <Icon icon="mdi:window-maximize" className={styles.cardHeaderIcon} aria-hidden />
              <div>
                <h3 id="widget-modal-preview-heading" className={styles.cardTitle}>
                  Prévia do modal
                </h3>
                <p className={styles.cardSubtitle}>
                  Simula o widget com combos ativos e visíveis, presets e limites de avulsos iguais ao script publicado.
                  Preço e mínimos ou máximos vêm da aba Valores.
                </p>
                {showPreviewRefresh && (
                  <button type="button" className={styles.previewRefreshBtn} onClick={() => void fetchPreviewSnapshot()}>
                    Tentar carregar dados de novo
                  </button>
                )}
              </div>
            </div>
            <div className={styles.cardBody}>
              <WidgetModalLivePreview
                buttonColor={effectiveButtonColor}
                buttonTextColor={effectiveTextColor}
                radiusPx={radiusPx}
                unitPrice={unitPrice}
                minExtra={minExtra}
                maxExtra={maxExtra}
                combos={combosSnapshot}
                preferServerComboPrices={preferServerComboPrices}
                isLoading={isPreviewLoading}
                errorMessage={previewLoadError}
              />
            </div>
          </section>
        </div>

        <section className={styles.snippetCard} aria-labelledby="widget-snippet-heading">
          <div className={styles.snippetToolbar}>
            <div className={styles.snippetHeading}>
              <Icon icon="mdi:code-tags" className={styles.snippetHeadingIcon} aria-hidden />
              <h3 id="widget-snippet-heading" className={styles.snippetTitle}>
                Código para copiar
              </h3>
            </div>
            <button type="button" className={styles.copyBtn} onClick={copyToClipboard}>
              <Icon icon={copied ? 'mdi:check' : 'mdi:content-copy'} aria-hidden />
              {copied ? 'Copiado' : 'Copiar código'}
            </button>
          </div>
          <pre className={styles.snippetCode}>{snippet}</pre>
        </section>
      </div>
    </div>
  )
}
